/*
  home.js: behaviour for home-* sections.

  - Add to cart goes through Impulse, exactly as on the product page
    (assets/pdp.js): each card or bundle form is handed to theme.AjaxProduct,
    which posts to /cart/add.js and fires ajaxProduct:added. Impulse's
    CartDrawer rebuilds and opens on that event, and Shopify's own analytics
    see the /cart/add.js request, so tracking fires as on any product page.
  - Hero slider: plain CSS scroll-snap; this file only drives the dots,
    arrows and the optional autoplay.
  - Product tabs: every panel is rendered by Liquid; this file switches them.
  - Rails (rows of cards): prev/next arrows on desktop when a row holds more
    cards than fit.
  - Sign-up: posts to Klaviyo's client subscriptions endpoint with the list
    ID from the section and the public key from the Klaviyo script on the
    page; otherwise the Shopify customer form submits as normal.
  Loaded with defer after theme.js, so theme.AjaxProduct exists by now.
*/
(function () {
  'use strict';

  // Every home-* section loads this file; run it once.
  if (window.homeScriptLoaded) return;
  window.homeScriptLoaded = true;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Add to cart ---------- */

  function bindCartForm(form) {
    if (form.dataset.homeCartBound) return;
    form.dataset.homeCartBound = 'true';
    var button = form.querySelector('[data-home-add]');
    if (!button) return;

    if (window.theme && typeof theme.AjaxProduct === 'function') {
      new theme.AjaxProduct(form, '[data-home-add]');
      return;
    }

    // Fallback if a theme update ever removes theme.AjaxProduct: same request,
    // same event, so the drawer still opens.
    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      button.classList.add('btn--loading');
      var cartAdd = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
      fetch(cartAdd, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }
      })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          button.classList.remove('btn--loading');
          if (data.status && data.status >= 400) {
            showFormError(form, data.description || data.message);
            return;
          }
          if (!document.getElementById('CartDrawer')) {
            window.location.href = (window.theme && theme.routes && theme.routes.cartPage) || '/cart';
            return;
          }
          form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: data, addToCartBtn: button },
            bubbles: true
          }));
        })
        .catch(function () { button.classList.remove('btn--loading'); });
    });
  }

  function showFormError(form, message) {
    var old = form.querySelector('.errors');
    if (old) old.remove();
    if (!message) return;
    var div = document.createElement('div');
    div.className = 'errors';
    div.textContent = typeof message === 'string' ? message : String(message);
    form.append(div);
  }

  /* ---------- Hero slider ---------- */

  function Slider(root) {
    this.root = root;
    this.track = root.querySelector('[data-vg-slides]');
    this.slides = Array.prototype.slice.call(this.track.children);
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-vg-dot]'));
    this.interval = Number(root.dataset.autoplay || 0) * 1000;
    this.paused = false;
    this.timer = null;

    var self = this;
    this.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { self.go(i); });
    });
    var prev = root.querySelector('[data-vg-prev]');
    var next = root.querySelector('[data-vg-next]');
    if (prev) prev.addEventListener('click', function () { self.go(self.current() - 1); });
    if (next) next.addEventListener('click', function () { self.go(self.current() + 1); });

    var ticking = false;
    this.track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; self.mark(); });
    }, { passive: true });

    this.mark();
    this.initAutoplay();
  }

  Slider.prototype = {
    current: function () {
      var width = this.track.clientWidth || 1;
      return Math.round(this.track.scrollLeft / width);
    },

    go: function (index) {
      var n = this.slides.length;
      var i = ((index % n) + n) % n;
      this.track.scrollTo({
        left: i * this.track.clientWidth,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    },

    mark: function () {
      var cur = this.current();
      this.dots.forEach(function (dot, i) { dot.setAttribute('aria-current', String(i === cur)); });
      this.slides.forEach(function (slide, i) {
        if (i === cur) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });
    },

    initAutoplay: function () {
      if (!this.interval || this.slides.length < 2) return;
      var self = this;
      var pause = function () { self.paused = true; };
      var resume = function () { self.paused = false; };
      this.root.addEventListener('mouseenter', pause);
      this.root.addEventListener('mouseleave', resume);
      this.root.addEventListener('focusin', pause);
      this.root.addEventListener('focusout', resume);
      this.root.addEventListener('touchstart', pause, { passive: true });
      this.timer = setInterval(function () {
        if (self.paused || reducedMotion.matches || document.hidden) return;
        if (window.Shopify && Shopify.designMode) return;
        self.go(self.current() + 1);
      }, this.interval);
    },

    // Theme editor: show the slide being edited.
    select: function (slide) {
      var i = this.slides.indexOf(slide);
      if (i > -1) this.go(i);
    }
  };

  /* ---------- Tabs ---------- */

  function Tabs(root) {
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    var self = this;
    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { self.select(i, false); });
      tab.addEventListener('keydown', function (evt) {
        var n = self.tabs.length;
        var to = null;
        if (evt.key === 'ArrowRight') to = (i + 1) % n;
        else if (evt.key === 'ArrowLeft') to = (i - 1 + n) % n;
        else if (evt.key === 'Home') to = 0;
        else if (evt.key === 'End') to = n - 1;
        if (to === null) return;
        evt.preventDefault();
        self.select(to, true);
      });
    });
  }

  Tabs.prototype.select = function (index, focus) {
    this.tabs.forEach(function (tab, i) {
      var on = i === index;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      var panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (!panel) return;
      panel.hidden = !on;
      if (on) {
        var rail = panel.querySelector('[data-vg-rail]');
        if (rail) { rail.scrollLeft = 0; updateRail(rail); }
      }
    });
    var current = this.tabs[index];
    if (focus) current.focus();
    current.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  };

  /* ---------- Rails ---------- */

  function updateRail(rail) {
    var wrap = rail.closest('[data-vg-rail-wrap]');
    if (!wrap) return;
    var max = rail.scrollWidth - rail.clientWidth;
    var overflow = max > 2;
    wrap.classList.toggle('is-overflowing', overflow);
    var prev = wrap.querySelector('[data-vg-rail-prev]');
    var next = wrap.querySelector('[data-vg-rail-next]');
    if (prev) prev.disabled = !overflow || rail.scrollLeft <= 2;
    if (next) next.disabled = !overflow || rail.scrollLeft >= max - 2;
  }

  function bindRail(rail) {
    if (rail.dataset.vgRailBound) return;
    rail.dataset.vgRailBound = 'true';
    var wrap = rail.closest('[data-vg-rail-wrap]');
    if (!wrap) return;
    var step = function (dir) {
      var card = rail.firstElementChild;
      var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      var by = card ? (card.getBoundingClientRect().width + gap) * Math.max(1, Math.floor(rail.clientWidth / (card.getBoundingClientRect().width + gap))) : rail.clientWidth;
      rail.scrollBy({ left: dir * by, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    var prev = wrap.querySelector('[data-vg-rail-prev]');
    var next = wrap.querySelector('[data-vg-rail-next]');
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    rail.addEventListener('scroll', function () { updateRail(rail); }, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(function () { updateRail(rail); }).observe(rail);
    updateRail(rail);
  }

  /* ---------- Sign-up (Klaviyo) ---------- */

  var KLAVIYO_REVISION = '2024-10-15';

  // The Klaviyo app embed loads klaviyo.js?company_id=<public key>.
  function klaviyoPublicKey() {
    var scripts = document.querySelectorAll('script[src*="klaviyo.js"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var key = new URL(scripts[i].src, location.href).searchParams.get('company_id');
        if (key) return key;
      } catch (e) { /* ignore malformed src */ }
    }
    return null;
  }

  function bindSignup(root) {
    if (root.dataset.vgSignupBound) return;
    root.dataset.vgSignupBound = 'true';
    var list = root.dataset.list;
    var form = root.querySelector('form');
    if (!list || !form) return; // No list ID: the Shopify customer form submits as normal.

    var input = form.querySelector('input[type="email"]');
    var button = form.querySelector('button[type="submit"]');
    var status = form.querySelector('[data-vg-signup-status]');

    function say(message, kind) {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('is-error', kind === 'error');
      status.classList.toggle('is-success', kind === 'success');
    }

    form.addEventListener('submit', function (evt) {
      var key = klaviyoPublicKey();
      if (!key || !input) return; // Fall back to the Shopify form.
      evt.preventDefault();

      var email = input.value.trim();
      if (!email || !input.checkValidity()) {
        input.setAttribute('aria-invalid', 'true');
        say(root.dataset.error, 'error');
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      button.classList.add('btn--loading');
      button.disabled = true;

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(key), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: root.dataset.source || 'Homepage sign-up',
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: { list: { data: { type: 'list', id: list } } }
          }
        })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Klaviyo ' + response.status);
          button.classList.remove('btn--loading');
          button.disabled = false;
          form.reset();
          say(root.dataset.success, 'success');
        })
        .catch(function () {
          // Klaviyo unreachable or refused: send it through Shopify instead.
          form.submit();
        });
    });
  }

  /* ---------- Init ---------- */

  var sliders = new WeakMap();

  function init(scope) {
    scope = scope || document;
    scope.querySelectorAll('.vg-home form.vg-cart-form').forEach(bindCartForm);
    scope.querySelectorAll('[data-vg-slider]').forEach(function (root) {
      if (sliders.has(root)) return;
      sliders.set(root, new Slider(root));
    });
    scope.querySelectorAll('[data-vg-tabs]').forEach(function (root) {
      if (root.dataset.vgTabsBound) return;
      root.dataset.vgTabsBound = 'true';
      new Tabs(root);
    });
    scope.querySelectorAll('[data-vg-rail]').forEach(bindRail);
    scope.querySelectorAll('[data-vg-signup]').forEach(bindSignup);
  }

  init(document);
  document.addEventListener('shopify:section:load', function (evt) { init(evt.target); });
  document.addEventListener('shopify:block:select', function (evt) {
    var slider = evt.target.closest && evt.target.closest('[data-vg-slider]');
    if (slider && sliders.has(slider)) sliders.get(slider).select(evt.target);
    var panel = evt.target.closest && evt.target.closest('[role="tabpanel"]');
    var tab = evt.target.matches && evt.target.matches('[role="tab"]') ? evt.target : null;
    if (tab) tab.click();
    else if (panel) {
      var owner = document.querySelector('[aria-controls="' + panel.id + '"]');
      if (owner) owner.click();
    }
  });
})();
